import { v4 as uuidv4 } from 'uuid';
import type { LLog, LLogRequest, LogLevel, LogMetadata } from './LLog.types';

const LOG_STYLES: Record<LogLevel, string> = {
  error:
    'color: #fff; background: #e53935; font-weight: bold; padding:2px 4px; border-radius:2px;',
  warn: 'color: #fff; background: #fbc02d; font-weight: bold; padding:2px 4px; border-radius:2px;',
  info: 'color: #fff; background: #1976d2; font-weight: bold; padding:2px 4px; border-radius:2px;',
  debug:
    'color: #fff; background: #43a047; font-weight: bold; padding:2px 4px; border-radius:2px;',
};

interface PerformanceWithMemory extends Performance {
  memory?: {
    usedJSHeapSize: number;
  };
}

class LLogService {
  private static instance: LLogService;
  private correlationId: string;
  private serviceName: string;
  private isLocal: boolean;

  private constructor() {
    this.correlationId = uuidv4();
    this.serviceName = 'front-leadsales';
    this.isLocal = import.meta.env.VITE_LOG_LOCAL_STYLE === 'true';
  }

  private shouldLog(level: LogLevel): boolean {
    const isProd = import.meta.env.PROD;
    if (isProd) {
      return level === 'error';
    }
    return true;
  }

  public static getInstance(): LLogService {
    if (!LLogService.instance) {
      LLogService.instance = new LLogService();
    }
    return LLogService.instance;
  }

  private formatLog(
    log: LLog | LLogRequest,
  ): [string, string, ...unknown[]] | string {
    const timestamp = new Date(log.timestamp).toLocaleTimeString();
    const level = log.level.toUpperCase();
    const context = `${log.context.component}.${log.context.method}.${log.context.operation}`;
    let output = `[${timestamp}] ${level} [${context}] ${log.message}`;
    const extra: unknown[] = [];

    if (log.metadata) {
      output += `\nMetadata:`;
      extra.push(log.metadata);
    }
    if (log.error) {
      output += `\nError: ${log.error.name} - ${log.error.message}`;
      if (log.error.stack) {
        output += `\nStack: ${log.error.stack}`;
      }
    }
    if (log.performance) {
      output += `\nPerformance: ${log.performance.duration}ns, ${log.performance.memory}bytes`;
    }

    if (this.isLocal) {
      // Usa estilos solo en local
      return [`%c${output}`, LOG_STYLES[log.level], ...extra];
    }
    // Producción: JSON plano
    return JSON.stringify(log);
  }

  private createBaseLog(
    level: LogLevel,
    context: LLog['context'],
    message: string,
    metadata?: LogMetadata,
    error?: LLog['error'],
    performance?: LLog['performance'],
  ): LLog {
    return {
      timestamp: new Date().toISOString(),
      level,
      service: this.serviceName,
      correlationId: this.correlationId,
      context,
      message,
      metadata,
      error,
      performance,
    };
  }

  public error(
    context: LLog['context'],
    message: string,
    error?: Error,
    metadata?: LogMetadata,
    performance?: LLog['performance'],
  ): void {
    if (!this.shouldLog('error')) return;
    const log = this.createBaseLog(
      'error',
      context,
      message,
      metadata,
      {
        name: error?.name || 'UnknownError',
        message: error?.message || 'Unknown error occurred',
        stack: error?.stack,
      },
      performance,
    );
    const formatted = this.formatLog(log);
    if (Array.isArray(formatted)) {
      console.error(...formatted);
    } else {
      console.error(formatted);
    }
  }

  public warn(
    context: LLog['context'],
    message: string,
    metadata?: LogMetadata,
    performance?: LLog['performance'],
  ): void {
    if (!this.shouldLog('warn')) return;
    const log = this.createBaseLog(
      'warn',
      context,
      message,
      metadata,
      undefined,
      performance,
    );
    const formatted = this.formatLog(log);
    if (Array.isArray(formatted)) {
      console.warn(...formatted);
    } else {
      console.warn(formatted);
    }
  }

  public info(
    context: LLog['context'],
    message: string,
    metadata?: LogMetadata,
    performance?: LLog['performance'],
  ): void {
    if (!this.shouldLog('info')) return;
    const log = this.createBaseLog(
      'info',
      context,
      message,
      metadata,
      undefined,
      performance,
    );
    const formatted = this.formatLog(log);
    if (Array.isArray(formatted)) {
      console.info(...formatted);
    } else {
      console.info(formatted);
    }
  }

  public debug(
    context: LLog['context'],
    message: string,
    metadata?: LogMetadata,
    performance?: LLog['performance'],
  ): void {
    if (!this.shouldLog('debug')) return;
    const log = this.createBaseLog(
      'debug',
      context,
      message,
      metadata,
      undefined,
      performance,
    );
    const formatted = this.formatLog(log);
    if (Array.isArray(formatted)) {
      console.debug(...formatted);
    } else {
      console.debug(formatted);
    }
  }

  public request(
    path: string,
    headers: Record<string, string>,
    body?: unknown,
  ): void {
    if (!this.shouldLog('debug')) return;
    const log: LLogRequest = {
      timestamp: new Date().toISOString(),
      level: 'debug',
      service: this.serviceName,
      correlationId: this.correlationId,
      context: {
        component: 'http',
        method: 'REQUEST',
        operation: 'IncomingRequest',
      },
      message: `Incoming request to ${path}`,
      metadata: {
        path,
        headers,
        body,
      },
    };
    const formatted = this.formatLog(log);
    if (Array.isArray(formatted)) {
      console.debug(...formatted);
    } else {
      console.debug(formatted);
    }
  }

  public setCorrelationId(id: string): void {
    this.correlationId = id;
  }

  public getCorrelationId(): string {
    return this.correlationId;
  }

  /**
   * Envuelve una función y loggea su performance (duración y memoria).
   * @param fn Función a medir
   * @param context Contexto del log
   * @param message Mensaje del log
   * @param metadata Metadata opcional
   * @param level Nivel de log (info|debug|warn|error)
   */
  public static async measure<T>(
    fn: () => Promise<T> | T,
    context: LLog['context'],
    message: string,
    metadata?: LogMetadata,
    level: LogLevel = 'info',
  ): Promise<T> {
    const start = performance.now();
    const perf = window?.performance as PerformanceWithMemory | undefined;
    const startMem = perf?.memory?.usedJSHeapSize;
    let result: T;
    let error: Error | undefined;
    try {
      result = await fn();
    } catch (err) {
      error = err as Error;
      throw err;
    } finally {
      const duration = performance.now() - start;
      const endMem = perf?.memory?.usedJSHeapSize;
      const memory = startMem && endMem ? endMem - startMem : 0;
      const performanceObj = { duration, memory };
      const logger = LLogService.getInstance();
      if (level === 'error' || error) {
        logger.error(context, message, error, metadata, performanceObj);
      } else if (level === 'warn') {
        logger.warn(context, message, metadata, performanceObj);
      } else if (level === 'debug') {
        logger.debug(context, message, metadata, performanceObj);
      } else {
        logger.info(context, message, metadata, performanceObj);
      }
    }
    return result!;
  }
}

const llog = LLogService.getInstance();
export { llog as LLog };
